import { useCallback, useEffect, useRef, useState } from "react";
import { CATEGORIZER_DISCLOSURE_VERSION } from "./CategorizerDisclosureModal";
import {
  isAcknowledgementCurrent,
  loadCategorizerDisclosure,
  saveCategorizerDisclosure,
} from "./categorizerDisclosureStore";

/**
 * useCategorizerDisclosure (ARC-15)
 *
 * Owns the acknowledgement gate in front of the four categorizer entry points
 * (Claude, ChatGPT, Gemini, Copy prompt only). All four gate on the same
 * acknowledgement.
 *
 * Usage:
 *   const disclosure = useCategorizerDisclosure();
 *   <button onClick={() => disclosure.run(() => launchLLM("claude"))}>
 *
 * `run(action)` either performs `action` straight away (a current
 * acknowledgement is on record) or holds it and opens the modal. The held
 * action fires only after a successful persist — a write that fails leaves the
 * modal open with a retryable error, so we never let a member through on an
 * acknowledgement we did not record.
 */
export default function useCategorizerDisclosure() {
  // null = not loaded yet, otherwise { version, acceptedAt } | false (none)
  const [ack, setAck] = useState(null);
  const [modal, setModal] = useState(null); // null | { mode: "gate" | "review" }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadRef = useRef(null);     // in-flight/settled load, so a fast click waits
  const pendingRef = useRef(null);  // the action the member originally clicked
  const triggerRef = useRef(null);  // element to hand focus back to on close
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const promise = loadCategorizerDisclosure().catch(() => null);
    loadRef.current = promise;
    promise.then((record) => {
      if (mountedRef.current) setAck(record || false);
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const acknowledged = isAcknowledgementCurrent(
    ack || null,
    CATEGORIZER_DISCLOSURE_VERSION
  );

  const rememberTrigger = () => {
    triggerRef.current =
      typeof document !== "undefined" &&
      document.activeElement &&
      document.activeElement !== document.body
        ? document.activeElement
        : null;
  };

  // Focus returns to the button that opened the modal (ARC-15 focus contract).
  const restoreFocus = () => {
    const el = triggerRef.current;
    triggerRef.current = null;
    if (el && typeof el.focus === "function" && document.contains(el)) el.focus();
  };

  const openGate = (action) => {
    rememberTrigger();
    pendingRef.current = typeof action === "function" ? action : null;
    setError(null);
    setModal({ mode: "gate" });
  };

  const run = useCallback(
    (action) => {
      // Already acknowledged this version — behave exactly as before the gate.
      if (isAcknowledgementCurrent(ack || null, CATEGORIZER_DISCLOSURE_VERSION)) {
        action?.();
        return;
      }
      // Not loaded yet: settle the read first rather than gate on a guess.
      if (ack === null) {
        rememberTrigger();
        const trigger = triggerRef.current;
        (loadRef.current || Promise.resolve(null)).then((record) => {
          if (!mountedRef.current) return;
          setAck(record || false);
          if (isAcknowledgementCurrent(record || null, CATEGORIZER_DISCLOSURE_VERSION)) {
            action?.();
            return;
          }
          triggerRef.current = trigger;
          pendingRef.current = typeof action === "function" ? action : null;
          setError(null);
          setModal({ mode: "gate" });
        });
        return;
      }
      openGate(action);
    },
    [ack]
  );

  /** Reopen the disclosure read-only from the "Data & privacy notice" link. */
  const openNotice = useCallback(() => {
    rememberTrigger();
    pendingRef.current = null;
    setError(null);
    setModal({ mode: "review" });
  }, []);

  /** Cancel / Escape / backdrop: persist nothing, fire nothing. */
  const cancel = useCallback(() => {
    if (saving) return;
    pendingRef.current = null;
    setError(null);
    setModal(null);
    restoreFocus();
  }, [saving]);

  const accept = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    let record;
    try {
      record = await saveCategorizerDisclosure(CATEGORIZER_DISCLOSURE_VERSION);
    } catch (err) {
      if (!mountedRef.current) return;
      setSaving(false);
      setError(
        err?.message ||
          "Couldn't save your acknowledgement. Please check your connection and try again."
      );
      return; // modal stays open; the original action does not fire
    }

    const stored = record || {
      version: CATEGORIZER_DISCLOSURE_VERSION,
      acceptedAt: null,
    };
    loadRef.current = Promise.resolve(stored);
    if (mountedRef.current) {
      setAck(stored);
      setSaving(false);
      setModal(null);
    }

    const action = pendingRef.current;
    pendingRef.current = null;
    restoreFocus();
    action?.();
  }, [saving]);

  return {
    /** true once a current-version acknowledgement is on record */
    acknowledged,
    /** true until the stored acknowledgement has been read */
    loading: ack === null,
    modal,
    saving,
    error,
    run,
    openNotice,
    cancel,
    accept,
  };
}
